<template>
  <div>
    <v-row>
      <v-overlay :value="isProcessing">
        <v-progress-circular
          indeterminate
          color="primary"
          :size="70"
          :width="7"
        ></v-progress-circular>
      </v-overlay>
      <v-dialog v-model="dialog" width="500px">
        <v-card class="confirmation-modal">
          <v-img
            class="modal-icon"
            width="84"
            :src="thumbsUpImage"
          />
          <h2 class="mt-8 mb-8">
            Thank you, your QT dog registration request has been submitted. You will be notified of approval.
          </h2>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogError" width="500px">
        <v-card class="confirmation-modal">
          <h2 class="mt-8 mb-8">
            {{ errorMessage }}
          </h2>
        </v-card>
      </v-dialog>
      <v-col md="5" class="pb-0 hidden-sm-and-down">
        <v-img
          height="100vh"
          :src="registerDogImage"
          class="sticky-image"
        />
      </v-col>
      <OwnerInformation
        v-if="currentPage === 1"
        :changePage="changePage"
        :changeCoOwnerInfo="changeCoOwner"
        :registerMember="registerMember"
        :renewMember="renewMember"
        :ownerInfo="ownerInformation"
        :coOwnerInfo="coOwnerInformation"
      />
      <DogInformation
        v-if="currentPage === 2"
        :changePage="changePage"
        :dogInfo="dogInformation"
      />
      <SummaryPayment
        v-if="currentPage === 3"
        :changePage="changePage"
        :submit="submit"
        :price="price"
      />
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

import OwnerInformation from "./Steps/OwnerInformation.vue";
import DogInformation from "./Steps/DogInformation.vue";
import SummaryPayment from "./Steps/SummaryPayment.vue";

import registerQTService from "@/services/portal/registerQTService";
import workflowService from "@/services/portal/workflowService";
import { getErrorMessage, getErrorResponse } from "@/services/errorHandler";

import { UserInfo } from "@/types/User";
import { CoOwnerResult, RegisterQTDTO } from "@/types/portal/Registration";
import AuthorizeNetResponseData from "@/types/AuthorizeNetResponseData";

type QTDogInformation = Omit<RegisterQTDTO["dogInformation"], "birthDate" | "gender"> & {
  birthDate: Temporal.PlainDate | null;
  gender: "M" | "F" | null;
};

const thumbsUpImage = require('@/assets/thumbs-up-duotone.svg');
const registerDogImage = require('@/assets/register-dog.png');

export default Vue.extend({
  name: "RegisterQTDog",
  components: {
    OwnerInformation,
    DogInformation,
    SummaryPayment,
  },
  mounted: function() {
    const userInfo = this.$store.state.userInfo;
    if (!!userInfo) this.initialize();

    this.getFees();
  },
  data: function () {
    return {
      thumbsUpImage,
      registerDogImage,
      isProcessing: false,
      currentPage: 1,

      dialog: false,
      dialogError: false,
      errorMessage: "",

      registerMember: false,
      renewMember: false,

      coOwnerInformation: [] as CoOwnerResult[],
      ownerInformation: {
        memberId: '',
        name: '',
        email: '',
        phoneNumber: '',
        address: {},
        signature: '',
      },
      dogInformation: {
        officialName: '',
        callName: '',
        gender: null,
        birthDate: null,
        breed: '',
        previousPointsEarned: '',
      } as QTDogInformation,
      price: null as number | null,
      responseStatus: {},
    }
  },
  methods: {
    initialize: async function() {
      this.isProcessing = true;

      const {
        hasNeverBeenMember,
        isActiveMember,
        memberId,
        firstName,
        lastName,
        email,
        phone,
        country,
        stateProvince,
        city,
        address,
        zipPostal,
        international
      } = this.userInfo;

      if (hasNeverBeenMember) this.registerMember = true;
      else if (!isActiveMember) this.renewMember = true;

      this.ownerInformation = {
        memberId,
        name: `${firstName} ${lastName}`,
        email,
        phoneNumber: phone,
        address: {
          address,
          international,
          city,
          stateProvince,
          zipPostal,
          country,
        },
        signature: '',
      };

      this.isProcessing = false;
    },
    getFees: async function() {
      this.isProcessing = true;

      try {
        const fee = await workflowService.getRegisterQTFees();
        if (fee !== null && !isNaN(fee)) {
          this.price = fee;
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    changePage(page: number) {
      this.currentPage = page;
    },
    changeCoOwner(coOwnerInfo: CoOwnerResult[]) {
      this.coOwnerInformation = coOwnerInfo;
    },
    submit: async function(paymentInfo: AuthorizeNetResponseData) {
      this.isProcessing = true;

      const { birthDate, gender } = this.dogInformation;
      if (!birthDate || !gender) {
        this.isProcessing = false;
        return;
      }

      try {
        this.responseStatus = await registerQTService.registerQTDog({
          paymentInfo,
          fee: this.price!,
          signature: this.ownerInformation.signature,
          coOwnersPersonIds: this.coOwnerInformation.map(coi => coi.personId),
          dogInformation: { ...this.dogInformation, birthDate, gender },
        });
      } catch (error) {
        this.responseStatus = error as object;
      } finally {
        this.isProcessing = false;
      }
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
  watch: {
    userInfo: function (newValue) {
      if (!!newValue) this.initialize();
    },
    responseStatus: function (newValue) {
      if (newValue?.success) {
        this.dialog = true;
      } else {
        this.dialogError = true;
        this.errorMessage = getErrorMessage(newValue);
      }
    },
    dialog: function (newValue) {
      if (!newValue) {
        this.isProcessing = false;

        this.$store.dispatch("loadPersonTasks");
        this.$navigate("/");
      }
    },
    dialogError: function (newValue) {
      if (!newValue && getErrorResponse(this.responseStatus)?.data?.paymentIssue) {
        this.isProcessing = false;
        this.$store.dispatch("loadPersonTasks");
        this.$navigate("/");
      } else if (!newValue) this.isProcessing = false;
    }
  }
});
</script>
